/**
 * React Flow pane click: dismiss the shapes menu; during placement,
 * delegate to text-note handler; otherwise select the topmost section under the
 * cursor (sections use pointer-events: none on the body) or fall through to
 * text-note placement passthrough.
 *
 * Note: pane clicks intentionally do NOT close expanded quick chats — those
 * only close via the panel close button or by clicking their icon/node.
 */

import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasPermissions } from "./useCanvasPermissions";

export interface UseCanvasPaneClickParams {
  isPlacing: boolean;
  isPlacingTextNote: boolean;
  handleTextNoteClick: (e: React.MouseEvent) => void;
  isPlacingDoc: boolean;
  handleDocClick: (e: React.MouseEvent) => void;
  isPlacingDeferredQuickCompose?: boolean;
  handleDeferredQuickComposeClick?: (e: React.MouseEvent) => void;
  /** When false (Pointer tool), pane clicks do not select sections. */
  paneSelectsOnClick?: boolean;
}

export function useCanvasPaneClick({
  isPlacing,
  isPlacingTextNote,
  handleTextNoteClick,
  isPlacingDoc,
  handleDocClick,
  isPlacingDeferredQuickCompose = false,
  handleDeferredQuickComposeClick,
  paneSelectsOnClick = true,
}: UseCanvasPaneClickParams) {
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow<Node>();
  const { isShapesMenuOpen, setIsShapesMenuOpen } = useCanvasUi();
  return useCallback(
    (e: React.MouseEvent) => {
      if (isShapesMenuOpen) {
        setIsShapesMenuOpen(false);
      }
      if (canEdit) {
        if (isPlacing) {
          if (isPlacingTextNote) handleTextNoteClick(e);
          if (isPlacingDoc) handleDocClick(e);
          if (isPlacingDeferredQuickCompose) {
            handleDeferredQuickComposeClick?.(e);
          }
          return;
        }
        if (!paneSelectsOnClick) {
          return;
        }
        const flowPos = reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        });
        const allNodes = reactFlowInstance.getNodes();
        const sectionAtPoint = allNodes.findLast((n) => {
          if (n.type !== "section") return false;
          const internal = reactFlowInstance.getInternalNode(n.id);
          const pos = internal?.internals.positionAbsolute ?? n.position;
          const w = n.width ?? 0;
          const h = n.height ?? 0;
          return (
            flowPos.x >= pos.x &&
            flowPos.y >= pos.y &&
            flowPos.x <= pos.x + w &&
            flowPos.y <= pos.y + h
          );
        });
        if (sectionAtPoint) {
          reactFlowInstance.setNodes((prev) =>
            prev.map((node) => ({
              ...node,
              selected: node.id === sectionAtPoint.id,
            })),
          );
          return;
        }
        handleTextNoteClick(e);
      }
    },
    [
      isShapesMenuOpen,
      setIsShapesMenuOpen,
      canEdit,
      reactFlowInstance,
      handleTextNoteClick,
      isPlacing,
      isPlacingTextNote,
      isPlacingDoc,
      handleDocClick,
      isPlacingDeferredQuickCompose,
      handleDeferredQuickComposeClick,
      paneSelectsOnClick,
    ],
  );
}
